import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useVendor } from '../../contexts/VendorContext';
import VendorLayout from '../../components/VendorLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ImageUploader from '../../components/ImageUploader';
import {
  ArrowLeft, Save, Loader2, Plus, X, Image as ImageIcon, Info, FileCheck, Package
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function VendorProductForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { vendor, token } = useVendor();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [broaderRegions, setBroaderRegions] = useState([]);
  
  // Variant management
  const [variants, setVariants] = useState([]);
  const [newVariant, setNewVariant] = useState({ name: '', weight: '', price: '', sale_price: '', inventory: '' });
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    sale_price: '',
    category_id: '',
    sku: '',
    inventory: '',
    weight: '',
    tags: [],
    images: [],
    region_name: '',
    region_description: '',
    broader_region: '',
    // International shipping
    international_shipping: false,
    // New product details fields
    brand: '',
    fssai_number: '',
    ingredients: '',
    net_quantity: '',
    container_name: '',
    manufacturer: '',
    manufacturer_address: '',
    manufacturing_date: '',
    shelf_life: '',
    storage_instructions: '',
    country_of_origin: 'India',
    contact_details: '',
    // GST field
    gst_rate: '',
    // Fun Facts & Health Benefits
    history_fact: '',
    health_benefits: ''
  });
  
  const [tagInput, setTagInput] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!vendor || !token) return;
    
    fetchCategories();
    fetchBroaderRegions();
    if (isEdit) {
      fetchProduct();
    }
  }, [vendor, token, id]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/collections`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBroaderRegions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/broader-regions`);
      setBroaderRegions(response.data || []);
    } catch (error) {
      console.error('Error fetching regions:', error);
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vendor/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const product = response.data.find(p => p.id === id);
      if (product) {
        setFormData({
          name: product.name || '',
          description: product.description || '',
          price: product.price?.toString() || '',
          sale_price: product.sale_price?.toString() || '',
          category_id: product.category_id || '',
          sku: product.sku || '',
          inventory: product.inventory?.toString() || '',
          weight: product.weight?.toString() || '',
          tags: product.tags || [],
          images: product.images || [],
          region_name: product.region_name || '',
          region_description: product.region_description || '',
          broader_region: product.broader_region || '',
          // International shipping
          international_shipping: product.international_shipping || false,
          // Product details fields
          brand: product.brand || '',
          fssai_number: product.fssai_number || '',
          ingredients: product.ingredients || '',
          net_quantity: product.net_quantity || '',
          container_name: product.container_name || '',
          manufacturer: product.manufacturer || '',
          manufacturer_address: product.manufacturer_address || '',
          manufacturing_date: product.manufacturing_date || '',
          shelf_life: product.shelf_life || '',
          storage_instructions: product.storage_instructions || '',
          country_of_origin: product.country_of_origin || 'India',
          contact_details: product.contact_details || '',
          gst_rate: product.gst_rate?.toString() || '',
          // Fun Facts & Health Benefits
          history_fact: product.history_fact || '',
          health_benefits: product.health_benefits || ''
        });
        // Load variants
        setVariants(product.variants || []);
      } else {
        toast.error('Product not found');
        navigate('/vendor/products');
      }
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const addImage = () => {
    if (imageUrl.trim() && !formData.images.includes(imageUrl.trim())) {
      setFormData(prev => ({ ...prev, images: [...prev.images, imageUrl.trim()] }));
      setImageUrl('');
    }
  };

  const removeImage = (imageToRemove) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imageToRemove)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.price || !formData.category_id) {
      toast.error('Please fill in required fields');
      return;
    }
    
    setSaving(true);
    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        sale_price: formData.sale_price ? parseFloat(formData.sale_price) : null,
        category_id: formData.category_id,
        sku: formData.sku || `VND-${Date.now()}`,
        inventory: parseInt(formData.inventory) || 0,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        tags: formData.tags,
        images: formData.images,
        region_name: formData.region_name,
        region_description: formData.region_description,
        broader_region: formData.broader_region,
        // International shipping
        international_shipping: formData.international_shipping,
        // Product variants
        variants: variants,
        // Product details fields
        brand: formData.brand || null,
        fssai_number: formData.fssai_number || null,
        ingredients: formData.ingredients || null,
        net_quantity: formData.net_quantity || null,
        container_name: formData.container_name || null,
        manufacturer: formData.manufacturer || null,
        manufacturer_address: formData.manufacturer_address || null,
        manufacturing_date: formData.manufacturing_date || null,
        shelf_life: formData.shelf_life || null,
        storage_instructions: formData.storage_instructions || null,
        country_of_origin: formData.country_of_origin || 'India',
        contact_details: formData.contact_details || null,
        // GST field
        gst_rate: formData.gst_rate ? parseFloat(formData.gst_rate) : null,
        // Fun Facts & Health Benefits
        history_fact: formData.history_fact || null,
        health_benefits: formData.health_benefits || null
      };
      
      if (isEdit) {
        await axios.put(
          `${API_URL}/api/vendor/products/${id}`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Product updated successfully');
      } else {
        await axios.post(
          `${API_URL}/api/vendor/products`,
          productData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Product created successfully');
      }
      
      navigate('/vendor/products');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <VendorLayout>
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      </VendorLayout>
    );
  }

  return (
    <VendorLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <Link to="/vendor/products">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Product' : 'Add New Product'}
          </h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {/* Basic Info */}
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter product name"
                    required
                    data-testid="product-name-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Describe your product..."
                    className="w-full min-h-[100px] p-3 border rounded-lg focus:ring-2 focus:ring-green-200 focus:border-green-400"
                    data-testid="product-description-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category_id">Category *</Label>
                    <select
                      id="category_id"
                      name="category_id"
                      value={formData.category_id}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-200"
                      required
                      data-testid="product-category-select"
                    >
                      <option value="">Select a category</option>
                      {categories.map(cat => (
                        <option key={cat._id || cat.id} value={cat._id || cat.id}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <Label htmlFor="brand">Brand Name</Label>
                    <Input
                      id="brand"
                      name="brand"
                      value={formData.brand}
                      onChange={handleChange}
                      placeholder="Your brand name"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="sku">SKU</Label>
                  <Input
                    id="sku"
                    name="sku"
                    value={formData.sku}
                    onChange={handleChange}
                    placeholder="Auto-generated if empty"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Pricing */}
            <Card>
              <CardHeader>
                <CardTitle>Pricing & Inventory</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="price">Price (₹) *</Label>
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="0.00"
                      required
                      data-testid="product-price-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="sale_price">Sale Price (₹)</Label>
                    <Input
                      id="sale_price"
                      name="sale_price"
                      type="number"
                      step="0.01"
                      value={formData.sale_price}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="inventory">Inventory *</Label>
                    <Input
                      id="inventory"
                      name="inventory"
                      type="number"
                      value={formData.inventory}
                      onChange={handleChange}
                      placeholder="0"
                      required
                      data-testid="product-inventory-input"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="weight">Weight (grams)</Label>
                    <Input
                      id="weight"
                      name="weight"
                      type="number"
                      value={formData.weight}
                      onChange={handleChange}
                      placeholder="0"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="gst_rate">GST Rate (%)</Label>
                    <select
                      id="gst_rate"
                      name="gst_rate"
                      value={formData.gst_rate}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg bg-white"
                    >
                      <option value="">Use store default</option>
                      <option value="0">0% (Exempt)</option>
                      <option value="5">5%</option>
                      <option value="12">12%</option>
                      <option value="18">18%</option>
                      <option value="28">28%</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Override store's default GST</p>
                  </div>
                </div>

                {/* International Shipping Toggle */}
                <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">Available for International Shipping</p>
                        <p className="text-sm text-gray-500">Enable this to allow worldwide delivery for this product</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.international_shipping}
                        onChange={(e) => setFormData(prev => ({ ...prev, international_shipping: e.target.checked }))}
                        className="sr-only peer"
                        data-testid="international-shipping-toggle"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Product Variants Section */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-600" />
                  Product Variants (Weight/Size Options)
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Add different weight or size options for this product (e.g., 100g, 250g, 500g)
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Existing Variants */}
                {variants.length > 0 && (
                  <div className="space-y-2">
                    <Label>Current Variants</Label>
                    <div className="space-y-2">
                      {variants.map((variant, index) => (
                        <div 
                          key={index} 
                          className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200"
                        >
                          <div className="flex items-center gap-4">
                            <span className="font-medium text-purple-800">{variant.name}</span>
                            <span className="text-sm text-gray-600">Weight: {variant.weight}g</span>
                            <span className="text-sm font-medium text-green-600">₹{variant.price}</span>
                            {variant.sale_price && (
                              <span className="text-sm text-orange-600">Sale: ₹{variant.sale_price}</span>
                            )}
                            <span className="text-sm text-gray-500">Stock: {variant.inventory}</span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => setVariants(variants.filter((_, i) => i !== index))}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Add New Variant */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Label className="mb-3 block">Add New Variant</Label>
                  <div className="grid grid-cols-5 gap-3">
                    <div>
                      <Input
                        placeholder="Name (e.g., 100g Pack)"
                        value={newVariant.name}
                        onChange={(e) => setNewVariant(prev => ({ ...prev, name: e.target.value }))}
                        data-testid="variant-name-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        placeholder="Weight (g)"
                        value={newVariant.weight}
                        onChange={(e) => setNewVariant(prev => ({ ...prev, weight: e.target.value }))}
                        data-testid="variant-weight-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        placeholder="Price (₹)"
                        value={newVariant.price}
                        onChange={(e) => setNewVariant(prev => ({ ...prev, price: e.target.value }))}
                        data-testid="variant-price-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        placeholder="Sale Price (₹)"
                        value={newVariant.sale_price}
                        onChange={(e) => setNewVariant(prev => ({ ...prev, sale_price: e.target.value }))}
                        data-testid="variant-sale-price-input"
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        placeholder="Stock"
                        value={newVariant.inventory}
                        onChange={(e) => setNewVariant(prev => ({ ...prev, inventory: e.target.value }))}
                        data-testid="variant-inventory-input"
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3"
                    onClick={() => {
                      if (!newVariant.name || !newVariant.weight || !newVariant.price) {
                        toast.error('Please fill variant name, weight and price');
                        return;
                      }
                      setVariants(prev => [...prev, {
                        name: newVariant.name,
                        weight: parseFloat(newVariant.weight),
                        price: parseFloat(newVariant.price),
                        sale_price: newVariant.sale_price ? parseFloat(newVariant.sale_price) : null,
                        inventory: parseInt(newVariant.inventory) || 0
                      }]);
                      setNewVariant({ name: '', weight: '', price: '', sale_price: '', inventory: '' });
                      toast.success('Variant added');
                    }}
                    data-testid="add-variant-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Variant
                  </Button>
                </div>

                {/* Quick Add Common Variants */}
                <div className="flex flex-wrap gap-2">
                  <span className="text-sm text-gray-500 mr-2">Quick add:</span>
                  {['100g', '250g', '500g', '1kg'].map((weight) => (
                    <Button
                      key={weight}
                      type="button"
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => setNewVariant(prev => ({ 
                        ...prev, 
                        name: `${weight} Pack`,
                        weight: weight === '1kg' ? '1000' : weight.replace('g', '')
                      }))}
                    >
                      {weight}
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Product Details - NEW SECTION */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCheck className="w-5 h-5 text-green-600" />
                  Product Details (Compliance & Info)
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  These details will be displayed on the product page for customer transparency
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* FSSAI and Compliance */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2 mb-3">
                    <Info className="w-4 h-4 text-green-600" />
                    <span className="font-medium text-green-800">Food Safety Compliance</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="fssai_number">FSSAI License Number</Label>
                      <Input
                        id="fssai_number"
                        name="fssai_number"
                        value={formData.fssai_number}
                        onChange={handleChange}
                        placeholder="e.g., 12345678901234"
                      />
                      <p className="text-xs text-gray-500 mt-1">Required for food products</p>
                    </div>
                    <div>
                      <Label htmlFor="ingredients">Ingredients</Label>
                      <Input
                        id="ingredients"
                        name="ingredients"
                        value={formData.ingredients}
                        onChange={handleChange}
                        placeholder="e.g., Sugar, Ghee, Flour, Nuts"
                      />
                    </div>
                  </div>
                </div>

                {/* Quantity & Packaging */}
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="net_quantity">Net Quantity</Label>
                    <Input
                      id="net_quantity"
                      name="net_quantity"
                      value={formData.net_quantity}
                      onChange={handleChange}
                      placeholder="e.g., 500g, 1 kg, 250 ml"
                    />
                  </div>
                  <div>
                    <Label htmlFor="container_name">Container/Pack Type</Label>
                    <select
                      id="container_name"
                      name="container_name"
                      value={formData.container_name}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-200"
                    >
                      <option value="">Select type</option>
                      <option value="Box">Box</option>
                      <option value="Pack">Pack</option>
                      <option value="Jar">Jar</option>
                      <option value="Pouch">Pouch</option>
                      <option value="Bottle">Bottle</option>
                      <option value="Can">Can</option>
                      <option value="Bag">Bag</option>
                      <option value="Unit">Unit</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="shelf_life">Shelf Life</Label>
                    <Input
                      id="shelf_life"
                      name="shelf_life"
                      value={formData.shelf_life}
                      onChange={handleChange}
                      placeholder="e.g., 6 months, 1 year"
                    />
                  </div>
                </div>

                {/* Manufacturer Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="manufacturer">Manufacturer Name</Label>
                    <Input
                      id="manufacturer"
                      name="manufacturer"
                      value={formData.manufacturer}
                      onChange={handleChange}
                      placeholder="Enter manufacturer name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="manufacturer_address">Manufacturer Address</Label>
                    <Input
                      id="manufacturer_address"
                      name="manufacturer_address"
                      value={formData.manufacturer_address}
                      onChange={handleChange}
                      placeholder="City, State"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="manufacturing_date">Manufacturing Date</Label>
                    <Input
                      id="manufacturing_date"
                      name="manufacturing_date"
                      value={formData.manufacturing_date}
                      onChange={handleChange}
                      placeholder="e.g., Fresh Stock, Jan 2025"
                    />
                  </div>
                  <div>
                    <Label htmlFor="country_of_origin">Country of Origin</Label>
                    <Input
                      id="country_of_origin"
                      name="country_of_origin"
                      value={formData.country_of_origin}
                      onChange={handleChange}
                      placeholder="India"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_details">Contact Details</Label>
                    <Input
                      id="contact_details"
                      name="contact_details"
                      value={formData.contact_details}
                      onChange={handleChange}
                      placeholder="Email or phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="storage_instructions">Storage Instructions</Label>
                  <Input
                    id="storage_instructions"
                    name="storage_instructions"
                    value={formData.storage_instructions}
                    onChange={handleChange}
                    placeholder="e.g., Store in cool, dry place. Refrigerate after opening."
                  />
                </div>
              </CardContent>
            </Card>

            {/* Region */}
            <Card>
              <CardHeader>
                <CardTitle>Region & Origin Story</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="region_name">Region Name</Label>
                    <Input
                      id="region_name"
                      name="region_name"
                      value={formData.region_name}
                      onChange={handleChange}
                      placeholder="e.g., Karaikudi, Mysore"
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="broader_region">Broader Region</Label>
                    <select
                      id="broader_region"
                      name="broader_region"
                      value={formData.broader_region}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-green-200"
                    >
                      <option value="">Select region</option>
                      {broaderRegions.map(region => (
                        <option key={region._id} value={region.code}>{region.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="region_description">Region Story / Fun Fact</Label>
                  <textarea
                    id="region_description"
                    name="region_description"
                    value={formData.region_description}
                    onChange={handleChange}
                    placeholder="Share the story about the product's origin, traditional significance, etc."
                    className="w-full min-h-[80px] p-3 border rounded-lg focus:ring-2 focus:ring-green-200"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Fun Facts & Health Benefits */}
            <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50/50 to-green-50/50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-amber-800">
                  <Info className="w-5 h-5" />
                  Fun Facts & Health Benefits
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Add interesting facts and health benefits to engage customers (optional but recommended)
                </p>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="history_fact" className="flex items-center gap-2 text-amber-700 font-medium">
                    <span className="text-lg">💡</span>
                    Did You Know? (Fun Fact)
                  </Label>
                  <textarea
                    id="history_fact"
                    name="history_fact"
                    value={formData.history_fact}
                    onChange={handleChange}
                    placeholder="E.g., Tirunelveli halwa's roots trace back over a century, with origin stories linking its rise to Rajput cooks invited by the Chokkampatti zamindar..."
                    className="w-full min-h-[100px] p-3 border border-amber-200 rounded-lg focus:ring-2 focus:ring-amber-300 bg-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">Share interesting history, cultural significance, or fun facts about the product</p>
                </div>
                
                <div>
                  <Label htmlFor="health_benefits" className="flex items-center gap-2 text-green-700 font-medium">
                    <span className="text-lg">💚</span>
                    Health Benefits
                  </Label>
                  <textarea
                    id="health_benefits"
                    name="health_benefits"
                    value={formData.health_benefits}
                    onChange={handleChange}
                    placeholder="E.g., Rich in antioxidants and natural energy boosters. Contains essential vitamins and minerals that support immunity and overall wellness..."
                    className="w-full min-h-[100px] p-3 border border-green-200 rounded-lg focus:ring-2 focus:ring-green-300 bg-white"
                  />
                  <p className="text-xs text-gray-500 mt-1">List any health benefits, nutritional value, or wellness advantages of the product</p>
                </div>
              </CardContent>
            </Card>

            {/* Images - Drag & Drop with AI Enhancement */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="w-5 h-5 text-green-600" />
                  Product Images
                </CardTitle>
                <p className="text-sm text-gray-500 mt-1">
                  Upload high-quality images. First image will be the main product image.
                </p>
              </CardHeader>
              <CardContent>
                <ImageUploader
                  images={formData.images}
                  onImagesChange={(newImages) => setFormData(prev => ({ ...prev, images: newImages }))}
                  maxImages={5}
                  showAIEnhance={true}
                />
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    placeholder="Add a tag"
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    className="flex-1"
                  />
                  <Button type="button" variant="outline" onClick={addTag}>
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                
                {formData.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                      >
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Submit */}
            <div className="flex gap-4">
              <Link to="/vendor/products" className="flex-1">
                <Button type="button" variant="outline" className="w-full">
                  Cancel
                </Button>
              </Link>
              <Button
                type="submit"
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={saving}
                data-testid="save-product-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {isEdit ? 'Update Product' : 'Create Product'}
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </VendorLayout>
  );
}
